import {Action, createReducer, on} from '@ngrx/store';
import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';

import * as ShipmentActivitiesActions from './shipment-activities.actions';
import {ShipmentActivity} from './shipment-activities.models';
import {common} from '@qwyk/models';

export const SHIPMENTACTIVITIES_FEATURE_KEY = 'shipmentActivities';

export interface State extends EntityState<ShipmentActivity> {
    selectedId?: string | number; // which ShipmentActivities record has been selected
    loading: boolean; // has the ShipmentActivities list been loaded
    error?: string | null; // last known error (if any)
    pagination?: common.PaginationMeta;
}

export interface ShipmentActivitiesPartialState {
    readonly [SHIPMENTACTIVITIES_FEATURE_KEY]: State;
}

const shipmentActivitiesSortComparer = (
    a: ShipmentActivity,
    b: ShipmentActivity
) => {
    return a.created_at > b.created_at ? -1 : 1;
};

export const shipmentActivitiesAdapter: EntityAdapter<ShipmentActivity> = createEntityAdapter<ShipmentActivity>(
    {sortComparer: shipmentActivitiesSortComparer}
);

export const initialState: State = shipmentActivitiesAdapter.getInitialState({
    // set initial required properties
    loading: false,
});

const shipmentActivitiesReducer = createReducer(
    initialState,
    on(ShipmentActivitiesActions.init, state => ({
        ...state,
        loading: true,
        error: null,
        pagination: null,
    })),
    on(
        ShipmentActivitiesActions.loadShipmentActivitiesSuccess,
        (state, {shipmentActivities, pagination}) =>
            shipmentActivitiesAdapter.setAll(shipmentActivities, {
                ...state,
                loading: false,
                pagination,
            })
    ),
    on(
        ShipmentActivitiesActions.loadShipmentActivitiesFailure,
        ShipmentActivitiesActions.loadMoreShipmentActivitiesFailure,
        ShipmentActivitiesActions.createShipmentActivitiesCommentFailure,
        (state, {error}) => ({...state, error, loading: false})
    ),
    on(
        ShipmentActivitiesActions.loadMoreShipmentActivities,
        ShipmentActivitiesActions.createShipmentActivitiesComment,
        state => ({
            ...state,
            loading: true,
            error: null,
        })
    ),
    on(
        ShipmentActivitiesActions.loadMoreShipmentActivitiesSuccess,
        (state, {shipmentActivities, pagination}) =>
            shipmentActivitiesAdapter.addMany(shipmentActivities, {
                ...state,
                loading: false,
                pagination,
            })
    ),
    on(
        ShipmentActivitiesActions.createShipmentActivitiesCommentSuccess,
        (state, {shipmentActivity}) =>
            shipmentActivitiesAdapter.removeOne(
                state.ids[state.ids.length - 1].toString(), // Remove the last item
                shipmentActivitiesAdapter.addOne(shipmentActivity, {
                    // Add the new one, sort comparer will move it to top
                    ...state,
                    loading: false,
                })
            )
    ),
    on(ShipmentActivitiesActions.reset, () => initialState),
    on(ShipmentActivitiesActions.dismissError, state => ({
        ...state,
        error: null,
    }))
);

export function reducer(state: State | undefined, action: Action) {
    return shipmentActivitiesReducer(state, action);
}
